import { convertDistance, getPreciseDistance } from 'geolib';
import { Set } from 'immutable';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { operationTypeConfig } from 'src/app/ops/config/operation-type-config';
import { ViewModelService } from 'src/app/shared/view-model.service';
import { SystemTimestampType } from 'src/graphql/generated/graphql.generated';
import { CardViewModel } from 'src/models/card-view-model';
import { RequestLocationViewModel } from 'src/models/request-location-view-model';
import { RequestPatientViewModel } from 'src/models/request-patient-view-model';
import { RequesterViewModel } from 'src/models/requester-view-model';
import { TransportOfferViewModel } from 'src/models/transport-offer-view-model';
import { selectViewModel } from 'src/models/view-model-helper';
import { selectRequesterByRequestId } from 'src/store/entityStores/requester-selectors';
import { selectTimestampByCardIdAndType } from 'src/store/entityStores/timestamp-selectors';
import {
  selectActiveTransportOfferByRequestId,
  selectAutoDeclineTransportOffersByRequestId,
  selectAvailableOrRespondedTransportOffersByRequestId,
  selectCancelledOrDeclinedTransportOffersByRequestId,
  selectFirstCandidateTransportOfferByRequestId,
  selectSkippedOrDeclinedTransportOffersByRequestId,
  selectTransportOffersByRequestId,
} from 'src/store/entityStores/transport-offer-selectors';
import { RequestRecord, RequestViewModelBase } from 'src/store/generated/request-record.generated';
import { hasCoordinates } from 'src/store/model-helpers';
import { getLatLng } from 'src/util/mapping/trip-leg-model-record';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { round } from 'lodash-es';
import { undefinedOrSwitchMap } from 'src/util/rxjs';
import {
  selectAllPatientsByRequestId,
  selectInitialDestinationByRequestId,
  selectRequestPatientByRequestId,
  selectTargetDestinationByRequestId,
} from '../store/entityStores/request-selectors';
import { selectOfferedOrAcceptedTransportByRequestId } from '../store/entityStores/transport-offer-selectors';

export class RequestViewModel extends RequestViewModelBase {
  public readonly card: CardViewModel;
  public readonly transportOfferIds$: Observable<Set<string>>;
  public readonly availableOrRespondedOfferIds$: Observable<Set<string>>;
  public readonly skippedOrDeclinedOfferIds$: Observable<Set<string>>;
  public readonly autoDeclineOfferIds$: Observable<Set<string>>;
  public readonly cancelledOrDeclinedOfferIds$: Observable<Set<string>>;
  public readonly offeredOrAcceptedTransportId$: Observable<string | undefined>;
  public readonly requestPatientIds$: Observable<Set<string>>;
  public readonly initialDestination$: Observable<RequestLocationViewModel | undefined>;
  public readonly targetDestination$: Observable<RequestLocationViewModel | undefined>;
  public readonly requestPatient$: Observable<RequestPatientViewModel | undefined>;

  public readonly activeOffer$: Observable<TransportOfferViewModel | undefined>;
  public readonly requestPatientIfExists$: Observable<RequestPatientViewModel>;
  public readonly requesterIfExists$: Observable<RequesterViewModel>;
  public readonly estimatedLoadedMileageInMeters$: Observable<number>;
  public readonly estimatedLoadedMileageInMiles$: Observable<number>;
  public readonly estimatedLoadedMileageInNauticalMiles$: Observable<number>;

  public readonly requester$: Observable<RequesterViewModel | undefined>;
  public readonly isWebOrderAndWaiting$: Observable<boolean>;
  public readonly firstCandidate$: Observable<TransportOfferViewModel | undefined>;

  get requestedOperationTypeConfig() {
    return operationTypeConfig[this.requestedOperationType];
  }

  get isWebOrder() {
    return this.card.apiWebhookSubscriptionId != null;
  }

  constructor(
    request: RequestRecord,
    card: CardViewModel,
    private readonly _model: ViewModelService,
  ) {
    super(request);
    this.card = card;

    this.transportOfferIds$ = _model.store
      .select(selectTransportOffersByRequestId(this.id))
      .pipe(map((offers) => offers.map((o) => o.id).toOrderedSet()));

    this.offeredOrAcceptedTransportId$ = _model.store
      .select(selectOfferedOrAcceptedTransportByRequestId(this.id))
      .pipe(map((offer) => (offer == null ? undefined : offer.id)));

    this.availableOrRespondedOfferIds$ = _model.store
      .select(selectAvailableOrRespondedTransportOffersByRequestId(this.id))
      .pipe(map((offers) => offers.map((o) => o.id).toOrderedSet()));

    this.skippedOrDeclinedOfferIds$ = _model.store
      .select(selectSkippedOrDeclinedTransportOffersByRequestId(this.id))
      .pipe(map((offers) => offers.map((o) => o.id).toOrderedSet()));

    this.autoDeclineOfferIds$ = _model.store
      .select(selectAutoDeclineTransportOffersByRequestId(this.id))
      .pipe(map((offers) => offers.map((o) => o.id).toOrderedSet()));

    this.cancelledOrDeclinedOfferIds$ = _model.store
      .select(selectCancelledOrDeclinedTransportOffersByRequestId(this.id))
      .pipe(map((offers) => offers.map((o) => o.id).toOrderedSet()));

    this.activeOffer$ = _model.store
      .select(selectActiveTransportOfferByRequestId(this.id))
      .pipe(undefinedOrSwitchMap((offer) => this.getTransportOffer(offer.id)));

    this.firstCandidate$ = _model.store
      .select(selectFirstCandidateTransportOfferByRequestId(this.id))
      .pipe(
        switchMap((to) => {
          if (to == null) {
            return of(undefined);
          }

          return this.getTransportOffer(to.id);
        }),
      );

    this.requestPatientIds$ = this._model.store
      .select(selectAllPatientsByRequestId(this.id))
      .pipe(map((patients) => patients.map((p) => p.id).toOrderedSet()));

    this.requestPatient$ = selectViewModel(
      _model.store,
      selectRequestPatientByRequestId(this.id),
      (requestPatient) => {
        return requestPatient == null
          ? undefined
          : new RequestPatientViewModel(requestPatient, this, this._model);
      },
    );

    this.requestPatientIfExists$ = this.requestPatient$.pipe(filterNull());

    this.requester$ = selectViewModel(
      _model.store,
      selectRequesterByRequestId(this.id),
      (requester) => {
        return requester == null ? undefined : new RequesterViewModel(requester, this, _model);
      },
    );

    this.requesterIfExists$ = this.requester$.pipe(filterNull());

    this.initialDestination$ = this._model.store
      .select(selectInitialDestinationByRequestId(this.id))
      .pipe(
        switchMap((rl) => (rl != null ? this._model.getRequestLocationById(rl.id) : of(undefined))),
      );
    this.targetDestination$ = this._model.store
      .select(selectTargetDestinationByRequestId(this.id))
      .pipe(
        switchMap((rl) => (rl != null ? this._model.getRequestLocationById(rl.id) : of(undefined))),
      );

    const isWebOrderAcknowledged$ = this._model.store
      .select(
        selectTimestampByCardIdAndType(this.card.id, SystemTimestampType.WebOrderAcknowledged),
      )
      .pipe(map((ts) => (ts == null ? false : true)));

    this.isWebOrderAndWaiting$ = combineLatest(of(this.isWebOrder), isWebOrderAcknowledged$).pipe(
      map(([isWebOrder, acknowledged]) => isWebOrder && !acknowledged),
    );

    this.estimatedLoadedMileageInMeters$ = combineLatest(
      this.initialDestination$.pipe(filterNull()),
      this.targetDestination$.pipe(filterNull()),
    ).pipe(
      filter(([initialDest, targetDest]) => {
        return (
          initialDest.address != null &&
          hasCoordinates(initialDest.address.coordinates) &&
          targetDest.address != null &&
          hasCoordinates(targetDest.address.coordinates)
        );
      }),
      map(([initialDest, targetDest]) => {
        const distanceInMeters = getPreciseDistance(getLatLng(initialDest), getLatLng(targetDest));
        return distanceInMeters;
      }),
    );

    this.estimatedLoadedMileageInMiles$ = this.estimatedLoadedMileageInMeters$.pipe(
      map((distanceInMeters) => round(convertDistance(distanceInMeters, 'mi'), 0)),
    );

    this.estimatedLoadedMileageInNauticalMiles$ = this.estimatedLoadedMileageInMeters$.pipe(
      map((distanceInMeters) => round(convertDistance(distanceInMeters, 'sm'), 0)),
    );
  }

  public getTransportOffer(id: string): Observable<TransportOfferViewModel | undefined> {
    return this._model.getTransportOfferById(id);
  }
}
